import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import DOMPurify from "dompurify";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SITE_URL = "https://ninfa.eu";

const HAS_HTML = /<\/?(?:h[1-6]|p|div|ul|ol|li|blockquote|a|strong|em|br|img|table|tr|td|th|figure|figcaption)\b/i;

function useArticleSEO(post) {
  useEffect(() => {
    if (!post) return;

    const slug = post.slug || post.id;
    const canonicalUrl = `${SITE_URL}/blog/${slug}`;
    const seoTitle = (post.seo_title || post.title || "").slice(0, 60);
    const seoDesc = (post.seo_description || post.excerpt || "").slice(0, 160).trim();
    const ogImage = post.og_image || post.image_url || "";
    const publishedDate = post.published_at || post.created_at || "";
    const updatedDate = post.updated_at || publishedDate;

    const origTitle = document.title;
    const originals = {};
    const addedTags = [];

    const setMeta = (attr, key, content) => {
      let el = document.querySelector(`meta[${attr}="${key}"]`);
      if (el) {
        originals[`${attr}:${key}`] = { el, prev: el.getAttribute("content") };
        if (content) el.setAttribute("content", content);
      } else if (content) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        el.setAttribute("content", content);
        document.head.appendChild(el);
        addedTags.push(el);
      }
    };

    setMeta("name", "description", seoDesc);
    setMeta("name", "author", post.author || "NINFA.EU");
    setMeta("name", "keywords", (post.seo_keywords || []).join(", ") || null);
    setMeta("property", "og:title", seoTitle);
    setMeta("property", "og:description", seoDesc);
    setMeta("property", "og:url", canonicalUrl);
    setMeta("property", "og:type", "article");
    setMeta("property", "og:image", ogImage || null);
    setMeta("property", "article:published_time", publishedDate);
    setMeta("property", "article:modified_time", updatedDate);
    setMeta("property", "article:author", post.author || "NINFA.EU");
    setMeta("property", "article:section", post.category || null);
    setMeta("name", "twitter:title", seoTitle);
    setMeta("name", "twitter:description", seoDesc);
    setMeta("name", "twitter:image", ogImage || null);

    document.title = `${seoTitle} | NINFA.EU`;

    const canonicalEl = document.querySelector('link[rel="canonical"]');
    const origCanonical = canonicalEl ? canonicalEl.getAttribute("href") : null;
    if (canonicalEl) canonicalEl.setAttribute("href", canonicalUrl);

    const schemaId = "article-schema-ld";
    let schemaEl = document.getElementById(schemaId);
    if (!schemaEl) { schemaEl = document.createElement("script"); schemaEl.id = schemaId; schemaEl.type = "application/ld+json"; document.head.appendChild(schemaEl); }
    const schemaData = {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      "headline": post.title,
      "description": seoDesc,
      "author": { "@type": "Person", "name": post.author || "NINFA.EU", "url": SITE_URL },
      "publisher": { "@type": "Organization", "name": "NINFA.EU", "url": SITE_URL },
      "datePublished": publishedDate,
      "dateModified": updatedDate,
      "mainEntityOfPage": { "@type": "WebPage", "@id": canonicalUrl },
      "articleSection": post.category || "",
      "keywords": post.seo_keywords || [],
      "url": canonicalUrl,
    };
    if (ogImage) schemaData.image = ogImage;
    schemaEl.textContent = JSON.stringify(schemaData);

    return () => {
      document.title = origTitle;
      for (const { el, prev } of Object.values(originals)) {
        if (prev != null) el.setAttribute("content", prev);
      }
      addedTags.forEach(el => el.remove());
      if (canonicalEl && origCanonical) canonicalEl.setAttribute("href", origCanonical);
      const schema = document.getElementById(schemaId);
      if (schema) schema.remove();
    };
  }, [post]);
}

function ArticleContent({ content }) {
  if (HAS_HTML.test(content)) {
    const clean = DOMPurify.sanitize(content, {
      ALLOWED_TAGS: [
        "h1","h2","h3","h4","h5","h6","p","br","strong","b","em","i","u",
        "a","ul","ol","li","blockquote","img","figure","figcaption",
        "table","thead","tbody","tr","th","td","span","div","hr","sup","sub"
      ],
      ALLOWED_ATTR: ["href","src","alt","title","target","rel","class","id","width","height"],
    });
    return (
      <div
        data-testid="article-content-html"
        className="article-prose"
        dangerouslySetInnerHTML={{ __html: clean }}
      />
    );
  }

  return (
    <div data-testid="article-content-text" className="article-prose">
      {content.split("\n\n").map((paragraph, i) => {
        const trimmed = paragraph.trim();
        if (!trimmed) return null;
        const isHeading = trimmed.length < 80 && !trimmed.includes(". ") && !trimmed.endsWith(".");
        if (isHeading) {
          return <h2 key={i}>{trimmed}</h2>;
        }
        return <p key={i}>{trimmed}</p>;
      })}
    </div>
  );
}

export default function BlogArticle() {
  const { id } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API}/blog/${id}`)
      .then(res => {
        if (!res.ok) throw new Error("Not found");
        return res.json();
      })
      .then(data => { setPost(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [id]);

  useArticleSEO(post);

  if (loading) return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-[#FF3366] border-t-transparent animate-spin" />
    </div>
  );

  if (!post) return (
    <div className="min-h-screen bg-[#050505] flex flex-col items-center justify-center px-6">
      <h1 className="font-heading text-4xl font-bold text-white">Articolo non trovato</h1>
      <Link to="/" className="mt-6 text-[#FF3366] font-mono text-sm hover:text-white transition-colors">
        TORNA ALLA HOME
      </Link>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="blog-article-page">
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#050505]/90 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-[1440px] mx-auto px-6 sm:px-12 flex items-center justify-between h-20">
          <Link to="/" className="font-heading text-2xl font-black tracking-tighter text-white hover:text-[#FF3366] transition-colors">
            NINFA
          </Link>
          <Link to="/#blog" className="flex items-center gap-2 text-[#A3A3A3] hover:text-white transition-colors font-mono text-xs tracking-wider">
            <ArrowLeft size={16} /> TORNA AL BLOG
          </Link>
        </div>
      </nav>

      <article className="pt-32 pb-24">
        <div className="max-w-4xl mx-auto px-6 sm:px-12">
          <div className="flex items-center gap-4 mb-6">
            <span className="font-mono text-xs tracking-[0.2em] text-[#FF3366]">{post.category?.toUpperCase()}</span>
            <span className="font-mono text-xs tracking-wider text-[#A3A3A3]">
              {new Date(post.created_at).toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" })}
            </span>
          </div>

          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-tight">
            {post.title}
          </h1>

          <p className="text-[#FF3366] font-mono text-sm tracking-wider mt-6">{post.author}</p>

          {post.image_url && (
            <div className="mt-10 w-full overflow-hidden border border-white/10">
              <img src={post.image_url} alt={post.title} className="w-full h-auto object-contain" />
            </div>
          )}

          <div className="mt-12 border-t border-white/10 pt-10">
            <ArticleContent content={post.content} />
          </div>

          <div className="mt-12 border-t border-white/10 pt-8">
            <p className="font-mono text-xs tracking-[0.2em] text-[#A3A3A3] mb-4">CONDIVIDI QUESTO ARTICOLO</p>
            <div className="flex gap-3">
              <a
                href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="border border-white/10 px-4 py-2 text-xs font-mono text-white/60 hover:text-[#FF3366] hover:border-[#FF3366]/50 transition-colors"
              >
                FACEBOOK
              </a>
              <a
                href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(window.location.href)}&text=${encodeURIComponent(post.title)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="border border-white/10 px-4 py-2 text-xs font-mono text-white/60 hover:text-[#FF3366] hover:border-[#FF3366]/50 transition-colors"
              >
                TWITTER
              </a>
              <a
                href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="border border-white/10 px-4 py-2 text-xs font-mono text-white/60 hover:text-[#FF3366] hover:border-[#FF3366]/50 transition-colors"
              >
                LINKEDIN
              </a>
            </div>
          </div>

          <div className="mt-12">
            <Link to="/#blog" className="inline-flex items-center gap-2 bg-[#FF3366] text-white font-bold uppercase tracking-widest px-6 py-3 text-xs hover:bg-white hover:text-black transition-colors">
              <ArrowLeft size={16} /> TUTTI GLI ARTICOLI
            </Link>
          </div>
        </div>
      </article>

      <footer className="bg-[#050505] border-t border-white/10 py-8">
        <div className="max-w-[1440px] mx-auto px-6 sm:px-12 text-center">
          <p className="text-[#A3A3A3] text-xs font-mono">
            &copy; {new Date().getFullYear()} NINFA.EU — P.IVA 18086141001
          </p>
        </div>
      </footer>
    </div>
  );
}
